import React from "react";
import { useTranslation } from "react-i18next";
import RuleBodyPage from "components/common/RuleBodyPage";
import { RuleModalBodyProps } from "components/common/RuleModal";

function SingleRoleRouletteRuleBody({ page }: RuleModalBodyProps) {
  const { t } = useTranslation();
  const phaseContainer =
    "flex items-center gap-3 p-3 bg-gray-800 rounded-lg mb-4";
  const iconContainer =
    "w-10 h-10 min-w-10 min-h-10 flex items-center justify-center rounded-full text-sm";

  return (
    <div className="relative flex">
      {/* Page 1: 게임 소개 및 게임 모드 */}
      <RuleBodyPage show={page === 0}>
        <div className="space-y-4">
          <div className="text-gray-100">{t("SingleRoleRoulette.rules.0")}</div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-purple-600`}>
              <i className="fas fa-sync-alt" />
            </div>
            <div>
              <div className="font-medium text-purple-300 mb-1">
                {t("SingleRoleRoulette.rules.1")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.2")}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-blue-600`}>
              <i className="fas fa-eye" />
            </div>
            <div>
              <div className="font-medium text-blue-300 mb-1">
                {t("SingleRoleRoulette.rules.3")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.4")}
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>

      {/* Page 1: 사용 방법 */}
      <RuleBodyPage show={page === 1}>
        <div className="text-xl font-bold text-purple-400 mb-4">
          {t("SingleRoleRoulette.rules.5")}
        </div>
        <div className="space-y-4">
          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-green-600`}>
              <i className="fas fa-users" />
            </div>
            <div>
              <div className="font-medium text-green-300 mb-1">
                {t("SingleRoleRoulette.rules.6")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.7")}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-blue-600`}>
              <i className="fa-solid fa-gamepad" />
            </div>
            <div>
              <div className="font-medium text-blue-300 mb-1">
                {t("SingleRoleRoulette.rules.8")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.9")}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-yellow-600`}>
              <i className="fas fa-cog" />
            </div>
            <div>
              <div className="font-medium text-yellow-300 mb-1">
                {t("SingleRoleRoulette.rules.10")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.11")}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-red-600`}>
              <i className="fas fa-play" />
            </div>
            <div>
              <div className="font-medium text-red-300 mb-1">
                {t("SingleRoleRoulette.rules.12")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.13")}
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>

      {/* Page 2: 주의사항 */}
      <RuleBodyPage show={page === 2}>
        <div className="space-y-4">
          <div className="bg-orange-900/20 border border-orange-700/30 p-4 rounded-lg">
            <div className="font-medium text-orange-300 mb-2">
              {t("SingleRoleRoulette.rules.15")}
            </div>
            <ul className="list-disc list-inside space-y-2 text-sm text-orange-200">
              <li>{t("SingleRoleRoulette.rules.16")}</li>
              <li>{t("SingleRoleRoulette.rules.17")}</li>
              <li>{t("SingleRoleRoulette.rules.18")}</li>
            </ul>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-purple-600`}>
              <i className="fas fa-vote-yea" />
            </div>
            <div>
              <div className="font-medium text-purple-300 mb-1">
                {t("SingleRoleRoulette.rules.19")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.20")}
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>

      {/* Page 3: 활용 팁 및 설정 예제 */}
      <RuleBodyPage show={page === 3}>
        <div className="space-y-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="font-medium text-green-300 mb-2">
              {t("SingleRoleRoulette.rules.21")}
            </div>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-300">
              <li>{t("SingleRoleRoulette.rules.22")}</li>
              <li>{t("SingleRoleRoulette.rules.23")}</li>
            </ul>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-indigo-600`}>
              <i className="fas fa-lightbulb" />
            </div>
            <div>
              <div className="font-medium text-indigo-300 mb-1">
                {t("SingleRoleRoulette.rules.24")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.25")
                  .split("\\n")
                  .map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index === 0 && <br />}
                    </React.Fragment>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>

      {/* Page 4: Avalon Guide */}
      <RuleBodyPage show={page === 4}>
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-purple-400 mb-4">
            {t("SingleRoleRoulette.rules.26")}
          </h3>

          <div className="text-gray-100">{t("SingleRoleRoulette.rules.27")}</div>

          {/* Game Overview */}
          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-indigo-600`}>
              <i className="fas fa-info-circle" />
            </div>
            <div>
              <div className="font-medium text-indigo-300 mb-1">
                {t("SingleRoleRoulette.rules.28")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.29")}
              </div>
            </div>
          </div>

          {/* Good Faction */}
          <div className="bg-blue-900/20 border border-blue-700/30 p-4 rounded-lg">
            <h4 className="text-blue-300 font-semibold mb-3">
              {t("SingleRoleRoulette.rules.30")}
            </h4>

            <div className="space-y-2">
              {/* Merlin */}
              <div className={`${phaseContainer} mb-2`}>
                <div className={`${iconContainer} bg-blue-600`}>
                  <span className="text-lg">🔮</span>
                </div>
                <div>
                  <div className="font-medium text-blue-300 mb-1">
                    {t("SingleRoleRoulette.rules.31")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.32")}
                  </div>
                </div>
              </div>

              {/* Percival */}
              <div className={`${phaseContainer} mb-2`}>
                <div className={`${iconContainer} bg-blue-500`}>
                  <span className="text-lg">🛡️</span>
                </div>
                <div>
                  <div className="font-medium text-blue-300 mb-1">
                    {t("SingleRoleRoulette.rules.33")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.34")}
                  </div>
                </div>
              </div>

              {/* Loyal Servant */}
              <div className={`${phaseContainer}`}>
                <div className={`${iconContainer} bg-blue-400`}>
                  <span className="text-lg">⚔️</span>
                </div>
                <div>
                  <div className="font-medium text-blue-300 mb-1">
                    {t("SingleRoleRoulette.rules.35")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.36")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Evil Faction */}
          <div className="bg-red-900/20 border border-red-700/30 p-4 rounded-lg">
            <h4 className="text-red-300 font-semibold mb-3">
              {t("SingleRoleRoulette.rules.37")}
            </h4>

            <div className="space-y-2">
              {/* Mordred */}
              <div className={`${phaseContainer} mb-2`}>
                <div className={`${iconContainer} bg-red-600`}>
                  <span className="text-lg">🗡️</span>
                </div>
                <div>
                  <div className="font-medium text-red-300 mb-1">
                    {t("SingleRoleRoulette.rules.38")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.39")}
                  </div>
                </div>
              </div>

              {/* Morgana */}
              <div className={`${phaseContainer} mb-2`}>
                <div className={`${iconContainer} bg-red-500`}>
                  <span className="text-lg">🔮</span>
                </div>
                <div>
                  <div className="font-medium text-red-300 mb-1">
                    {t("SingleRoleRoulette.rules.40")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.41")}
                  </div>
                </div>
              </div>

              {/* Assassin */}
              <div className={`${phaseContainer} mb-2`}>
                <div className={`${iconContainer} bg-red-700`}>
                  <span className="text-lg">🗡️</span>
                </div>
                <div>
                  <div className="font-medium text-red-300 mb-1">
                    {t("SingleRoleRoulette.rules.42")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.43")}
                  </div>
                </div>
              </div>

              {/* Oberon */}
              <div className={`${phaseContainer} mb-2`}>
                <div className={`${iconContainer} bg-red-400`}>
                  <span className="text-lg">👤</span>
                </div>
                <div>
                  <div className="font-medium text-red-300 mb-1">
                    {t("SingleRoleRoulette.rules.44")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.45")}
                  </div>
                </div>
              </div>

              {/* Minion */}
              <div className={`${phaseContainer}`}>
                <div className={`${iconContainer} bg-red-300`}>
                  <span className="text-lg">⚔️</span>
                </div>
                <div>
                  <div className="font-medium text-red-300 mb-1">
                    {t("SingleRoleRoulette.rules.46")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.47")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* App Usage */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h4 className="text-green-300 font-semibold mb-2">
              {t("SingleRoleRoulette.rules.48")}
            </h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300">
              <li>{t("SingleRoleRoulette.rules.49")}</li>
              <li>{t("SingleRoleRoulette.rules.50")}</li>
              <li>{t("SingleRoleRoulette.rules.51")}</li>
            </ol>
          </div>

          {/* Recommended Setup */}
          <div className="bg-green-900/20 border border-green-700/30 p-4 rounded-lg">
            <h4 className="text-green-300 font-semibold mb-2">
              {t("SingleRoleRoulette.rules.52")}
            </h4>
            <ul className="space-y-1 text-sm text-gray-300">
              <li>• {t("SingleRoleRoulette.rules.53")}</li>
              <li>• {t("SingleRoleRoulette.rules.54")}</li>
              <li>• {t("SingleRoleRoulette.rules.55")}</li>
              <li>• {t("SingleRoleRoulette.rules.56")}</li>
            </ul>
          </div>
        </div>
      </RuleBodyPage>

      {/* Page 5: Mafia Guide */}
      <RuleBodyPage show={page === 5}>
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-red-400 mb-4">
            {t("SingleRoleRoulette.rules.57")}
          </h3>

          <div className="text-gray-100">{t("SingleRoleRoulette.rules.58")}</div>

          {/* Game Overview */}
          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-indigo-600`}>
              <i className="fas fa-info-circle" />
            </div>
            <div>
              <div className="font-medium text-indigo-300 mb-1">
                {t("SingleRoleRoulette.rules.59")}
              </div>
              <div className="text-sm text-gray-300">
                {t("SingleRoleRoulette.rules.60")}
              </div>
            </div>
          </div>

          {/* Citizen Faction */}
          <div className="bg-green-900/20 border border-green-700/30 p-4 rounded-lg">
            <h4 className="text-green-300 font-semibold mb-3">
              {t("SingleRoleRoulette.rules.61")}
            </h4>

            <div className="space-y-2">
              {/* Citizen */}
              <div className={`${phaseContainer} mb-2`}>
                <div className={`${iconContainer} bg-green-600`}>
                  <span className="text-lg">👤</span>
                </div>
                <div>
                  <div className="font-medium text-green-300 mb-1">
                    {t("SingleRoleRoulette.rules.62")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.63")}
                  </div>
                </div>
              </div>

              {/* Police */}
              <div className={`${phaseContainer} mb-2`}>
                <div className={`${iconContainer} bg-blue-600`}>
                  <span className="text-lg">👮</span>
                </div>
                <div>
                  <div className="font-medium text-blue-300 mb-1">
                    {t("SingleRoleRoulette.rules.64")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.65")}
                  </div>
                </div>
              </div>

              {/* Doctor */}
              <div className={`${phaseContainer}`}>
                <div className={`${iconContainer} bg-cyan-600`}>
                  <span className="text-lg">💉</span>
                </div>
                <div>
                  <div className="font-medium text-cyan-300 mb-1">
                    {t("SingleRoleRoulette.rules.66")}
                  </div>
                  <div className="text-sm text-gray-300">
                    {t("SingleRoleRoulette.rules.67")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mafia Faction */}
          <div className="bg-red-900/20 border border-red-700/30 p-4 rounded-lg">
            <h4 className="text-red-300 font-semibold mb-3">
              {t("SingleRoleRoulette.rules.68")}
            </h4>

            <div className={`${phaseContainer}`}>
              <div className={`${iconContainer} bg-red-600`}>
                <span className="text-lg">🔫</span>
              </div>
              <div>
                <div className="font-medium text-red-300 mb-1">
                  {t("SingleRoleRoulette.rules.69")}
                </div>
                <div className="text-sm text-gray-300">
                  {t("SingleRoleRoulette.rules.70")}
                </div>
              </div>
            </div>
          </div>

          {/* Gameplay Flow */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h4 className="text-purple-300 font-semibold mb-2">
              {t("SingleRoleRoulette.rules.71")}
            </h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300">
              <li>{t("SingleRoleRoulette.rules.72")}</li>
              <li>{t("SingleRoleRoulette.rules.73")}</li>
              <li>{t("SingleRoleRoulette.rules.74")}</li>
            </ol>
          </div>

          {/* Offline Usage */}
          <div className="bg-amber-900/20 border border-amber-700/30 p-4 rounded-lg">
            <h4 className="text-amber-300 font-semibold mb-2">
              {t("SingleRoleRoulette.rules.75")}
            </h4>
            <p className="text-sm text-gray-300">
              {t("SingleRoleRoulette.rules.76")}
            </p>
          </div>

          {/* Recommended Setup */}
          <div className="bg-green-900/20 border border-green-700/30 p-4 rounded-lg">
            <h4 className="text-green-300 font-semibold mb-2">
              {t("SingleRoleRoulette.rules.77")}
            </h4>
            <ul className="space-y-1 text-sm text-gray-300">
              <li>• {t("SingleRoleRoulette.rules.78")}</li>
              <li>• {t("SingleRoleRoulette.rules.79")}</li>
              <li>• {t("SingleRoleRoulette.rules.80")}</li>
            </ul>
          </div>
        </div>
      </RuleBodyPage>
    </div>
  );
}

export default SingleRoleRouletteRuleBody;
